import { useEffect, useState } from "react";
import Navbar2 from "../components/navbar/Navbar2";
import AOS from "aos";
import "aos/dist/aos.css";
import { api } from "../api";
import { parseJWT } from "../utils/jwtUtils";

export default function BetHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Get player info
  const token = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo"))
    : null;
  const userInfo = parseJWT(token?.token);

  const playerPhone = userInfo?.phone;

  // Initialize animations
  useEffect(() => {
    AOS.init({
      offset: 100,
      duration: 500,
      easing: "ease-in-sine",
      delay: 100,
    });
    AOS.refresh();
  }, []);

  // Fetch and format history data
  useEffect(() => {
    let isMounted = true;

    const fetchHistory = async () => {
      try {
        const res = await api.get(`/api/history/${playerPhone}`);
        const raw = Array.isArray(res.data) ? res.data : [];

        const formatted = raw.map((item, index) => {
          // Parse players and result fields safely
          let results = [];
          let players = [];
          try {
            results = JSON.parse(item.result || "[]");
          } catch (err) {
            console.warn("Failed to parse item.result:", item.result);
          }
          try {
            players = JSON.parse(item.players || "[]");
          } catch (err) {
            console.warn("Failed to parse item.players:", item.players);
          }

          // Filter this player's cards
          const yourCards =
            results
              .filter((r) => r.playerPhone === playerPhone)
              .map((r) => r.selectedCardNumber)
              .join(" , ") || "—";

          // Determine if the player won any card
          const youWon = results.some(
            (r) => r.playerPhone === playerPhone && r.result === "win",
          );

          // Format date
          const dateObj = item.createdAt ? new Date(item.createdAt) : null;
          const formattedDate = dateObj
            ? `${dateObj.getDate().toString().padStart(2, "0")}/${(
                dateObj.getMonth() + 1
              )
                .toString()
                .padStart(2, "0")}/${dateObj.getFullYear()}`
            : "—";

          return {
            no: index + 1,
            stake: item.stake ? `${item.stake} Birr` : "—",
            win: item.winAmount ? `${item.winAmount} Birr` : "—",
            winner: item.winnerCardNumber || "—",
            yours: yourCards,
            date: formattedDate,
            result: youWon ? "Won" : "Lost",
          };
        });

        if (isMounted) setHistory(formatted);
      } catch (err) {
        console.error("❌ Error fetching history:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchHistory();
    return () => {
      isMounted = false;
    };
  }, [playerPhone]);

  // Pagination logic
  const totalPages = Math.ceil(history.length / itemsPerPage);
  const paginatedData = history.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <>
      {/* Navbar */}
      <div className="fixed left-0 right-0 top-0 z-50 bg-gradient-to-l from-violet-900 via-violet-800 to-violet-900">
        <Navbar2 />
      </div>

      {/* Page content */}
      <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#020617] flex flex-col items-center py-10 px-2 sm:px-4 pt-[100px] text-white w-full">
        <h1 className="text-3xl font-semibold mb-8 border-b border-violet-500 pb-3 text-center font-serif">
          Bet History
        </h1>

        {loading ? (
          <div className="flex justify-center items-center h-40">
            <p className="text-gray-400 font-serif">
              Loading your game history...
            </p>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center text-gray-400 font-serif">
            እስካሁን ምንም የጨዋታ መረጃ አልተገኘም።
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto w-full max-w-6xl bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-xl">
              <table className="w-full text-sm text-center border-collapse">
                <thead className="bg-gradient-to-r from-blue-900/60 via-blue-800/60 to-blue-900/60 text-yellow-400">
                  <tr>
                    <th className="px-1 md:px-4 py-3 border border-white/10 rounded-tl-2xl font-serif">
                      No
                    </th>
                    {/* <th className="px-1 md:px-4 py-3 border border-white/10">
                      Stake
                    </th> */}
                    <th className="px-1 md:px-4 py-3 border border-white/10 font-serif">
                      Game Winning
                    </th>
                    <th className="px-1 md:px-4 py-3 border border-white/10 font-serif">
                      Winner Card
                    </th>
                    <th className="px-1 md:px-4 py-3 border border-white/10 font-serif">
                      Your Card
                    </th>
                    {/* <th className="px-4 py-3 border border-white/10">Your Cards</th> */}
                    <th className="px-1 md:px-4 py-3 border border-white/10 font-serif">
                      Date
                    </th>
                    <th className="px-1 md:px-4 py-3 border border-white/10 rounded-tr-2xl font-serif">
                      Result
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedData.map((row, i) => (
                    <tr
                      key={i}
                      className={`transition-all duration-200 ${
                        i % 2 === 0 ? "bg-white/5" : "bg-white/10"
                      } hover:bg-white/20`}
                    >
                      <td className="py-3 border border-white/10">{row.no}</td>
                      {/* <td className="py-3 border border-white/10 text-blue-300 font-semibold">
                        {row.stake}
                      </td> */}
                      <td className="py-3 border border-white/10 text-yellow-300 font-semibold">
                        {row.win}
                      </td>
                      <td className="py-3 border border-white/10">
                        <span className="bg-green-600/80 text-white px-2 py-1 rounded-md text-sm font-semibold">
                          {row.winner}
                        </span>
                      </td>
                      <td className="py-3 border border-white/10">
                        <span className="bg-green-600/80 text-white px-2 py-1 rounded-md text-sm font-semibold">
                          {row.yours}
                        </span>
                      </td>
                      <td className="py-3 border border-white/10 text-gray-300">
                        {row.date}
                      </td>
                      <td
                        className={`py-3 border border-white/10 font-bold ${
                          row.result === "Won"
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        {row.result}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-center items-center mt-6 space-x-2 pb-4 flex-wrap">
              <button
                onClick={() => goToPage(currentPage - 1)}
                className="px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-md text-white"
              >
                «
              </button>

              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => goToPage(i + 1)}
                  className={`px-3 py-2 rounded-md font-semibold ${
                    currentPage === i + 1
                      ? "bg-gradient-to-r from-blue-600 to-indigo-700 text-white"
                      : "bg-white/10 hover:bg-white/20 text-white border border-white/20"
                  }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() => goToPage(currentPage + 1)}
                className="px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-md text-white"
              >
                »
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
